import { DONATION_FREQUENCY, PAYMENT_TYPE } from '3-components/Donations/libs/common';
import { type DonationFrequencyType } from '3-components/Donations/types';
import queryString from 'query-string';
import { decryptData } from 'utils/encryption';
import { create } from 'zustand';

interface DonationStore {
    donationData: {
        submissionKey: string;
        currency: string;
        frequency: DonationFrequencyType;
        amount: number;
        fee: number;
        payment: string;
        showDonationNudge?: boolean;
    };
    initializeDonationData: () => void;
    setDonationData: (params: {
        amount?: number;
        fee?: number;
        frequency?: string;
        payment?: string;
        submissionKey?: string;
    }) => void;
    getTotalAmount: () => number;
}

export const useDonationStore = create<DonationStore>((set, get) => {
    return {
        donationData: {
            submissionKey: '',
            currency: 'AUD',
            amount: 0,
            fee: 0,
            frequency: 'once',
            payment: '',
            showDonationNudge: true,
        },
        initializeDonationData: (): void => {
            const parsedQueryUrl = queryString.parse(window.location.search);

            if (parsedQueryUrl?.token) {
                set(state => {
                    return {
                        donationData: {
                            ...state.donationData,
                            payment: PAYMENT_TYPE.PAYPAL,
                        },
                    };
                });
            }

            // set donation amount from url param
            if (parsedQueryUrl?.amount) {
                const queryAmount = parseFloat(parsedQueryUrl.amount as string);

                if (!isNaN(queryAmount)) {
                    set(state => {
                        return {
                            donationData: {
                                ...state.donationData,
                                amount: queryAmount,
                            },
                        };
                    });
                }
            }

            if (parsedQueryUrl?.donationFee) {
                const queryDonationFee = parseFloat(parsedQueryUrl.donationFee as string);

                if (!isNaN(queryDonationFee)) {
                    set(state => {
                        return {
                            donationData: {
                                ...state.donationData,
                                fee: queryDonationFee,
                            },
                        };
                    });
                }
            }

            // set donation type from url param
            if (parsedQueryUrl?.type) {
                const VALID_VALUES = Object.values(DONATION_FREQUENCY);
                const queryType = VALID_VALUES.includes(parsedQueryUrl.type as string) ? parsedQueryUrl.type as string : 'once';

                set(state => {
                    return {
                        donationData: {
                            ...state.donationData,
                            frequency: queryType as DonationFrequencyType,
                        },
                    };
                });
            }

            if (parsedQueryUrl?.payment) {
                try {
                    const paymentData = decryptData(parsedQueryUrl.payment as string) as string;

                    set(state => {
                        return {
                            donationData: {
                                ...state.donationData,
                                payment: paymentData,
                            },
                        };
                    });
                } catch (error) {
                    console.debug('Invalid payment code');
                }
            }
        },
        setDonationData: ({ amount, fee, frequency, payment, submissionKey }): void => {
            set(state => {
                return {
                    donationData: {
                        ...state.donationData,
                        ...(amount && { amount }),
                        ...(fee !== undefined && { fee }),
                        ...(frequency && { frequency: frequency as DonationFrequencyType }),
                        ...(payment && { payment }),
                        ...(submissionKey && { submissionKey }),
                    },
                };
            });
        },
        getTotalAmount: (): number => {
            const { amount, fee } = get().donationData;

            const totalAmount = amount + fee;

            return totalAmount;
        },
    };
});

